/**
 * Template Designer — internal types
 *
 * Mirrors @boox/report-engine types but kept local
 * to avoid coupling the UI to the package at import time.
 */

// ── Palette element descriptors ──────────────────────────────

export type PaletteElementType =
  | 'Label'
  | 'Field'
  | 'Image'
  | 'Line'
  | 'Rectangle'
  | 'Table'
  | 'Barcode'
  | 'Checkbox'
  | 'SummaryField'
  | 'PageNumber';

export interface PaletteItem {
  type: PaletteElementType;
  label: string;
  icon: string; // emoji
  description: string;
  defaultWidth: number;
  defaultHeight: number;
}

// ── Canvas element (runtime) ─────────────────────────────────

export interface DesignElement {
  id: string;
  type: PaletteElementType;
  x: number;
  y: number;
  width: number;
  height: number;
  /** element-specific props */
  props: Record<string, unknown>;
}

// ── Band ─────────────────────────────────────────────────────

export type BandType =
  | 'ReportHeader'
  | 'PageHeader'
  | 'GroupHeader'
  | 'Detail'
  | 'SubDetail'
  | 'GroupFooter'
  | 'PageFooter'
  | 'ReportFooter';

export interface DesignBand {
  id: string;
  type: BandType;
  height: number;
  elements: DesignElement[];
}

// ── Template (design-time) ───────────────────────────────────

export type PageSize = 'A4' | 'A3' | 'Letter' | 'Legal';
export type Orientation = 'portrait' | 'landscape';

export interface DesignTemplate {
  id: string;
  name: string;
  pageSize: PageSize;
  orientation: Orientation;
  margins: { top: number; right: number; bottom: number; left: number };
  bands: DesignBand[];
}

// ── Actions / Events ─────────────────────────────────────────

export type DesignerAction =
  | { type: 'SELECT_ELEMENT'; bandId: string; elementId: string }
  | { type: 'DESELECT' }
  | { type: 'MOVE_ELEMENT'; elementId: string; x: number; y: number }
  | { type: 'RESIZE_ELEMENT'; elementId: string; width: number; height: number }
  | { type: 'UPDATE_ELEMENT_PROPS'; props: Record<string, unknown> }
  | { type: 'ADD_ELEMENT'; bandId: string; element: DesignElement }
  | { type: 'DELETE_ELEMENT'; bandId: string; elementId: string }
  | { type: 'ADD_BAND'; band: DesignBand }
  | { type: 'REMOVE_BAND'; bandId: string }
  | { type: 'RESIZE_BAND'; bandId: string; height: number }
  | { type: 'REORDER_BANDS'; bandIds: string[] }
  | { type: 'UPDATE_TEMPLATE_META'; meta: Partial<Pick<DesignTemplate, 'name' | 'pageSize' | 'orientation' | 'margins'>> }
  | { type: 'UNDO' }
  | { type: 'LOAD_TEMPLATE'; template: DesignTemplate };

// ── Palette library ──────────────────────────────────────────

export const PALETTE_ITEMS: PaletteItem[] = [
  { type: 'Label',        label: 'Text Label',    icon: '🔤', description: 'Static text', defaultWidth: 120, defaultHeight: 20 },
  { type: 'Field',        label: 'Data Field',    icon: '📊', description: 'Bound to data source', defaultWidth: 120, defaultHeight: 20 },
  { type: 'Image',        label: 'Image',         icon: '🖼️', description: 'Logo / photo', defaultWidth: 80, defaultHeight: 60 },
  { type: 'Table',        label: 'Table',         icon: '📋', description: 'Data table with columns', defaultWidth: 500, defaultHeight: 100 },
  { type: 'Line',         label: 'Line',          icon: '➖', description: 'Horizontal / vertical line', defaultWidth: 200, defaultHeight: 2 },
  { type: 'Rectangle',    label: 'Rectangle',     icon: '⬜', description: 'Box / background', defaultWidth: 150, defaultHeight: 60 },
  { type: 'Barcode',      label: 'Barcode / QR',  icon: '📱', description: 'Barcode or QR code', defaultWidth: 100, defaultHeight: 40 },
  { type: 'Checkbox',     label: 'Checkbox',      icon: '☑️', description: 'Yes/No checkbox', defaultWidth: 80, defaultHeight: 18 },
  { type: 'SummaryField', label: 'Summary',       icon: '∑',  description: 'Sum, Count, Avg', defaultWidth: 100, defaultHeight: 20 },
  { type: 'PageNumber',   label: 'Page Number',   icon: '#️⃣', description: 'Page X of Y', defaultWidth: 80, defaultHeight: 16 },
];

// ── Band type display ────────────────────────────────────────

export const BAND_TYPE_CONFIG: Record<BandType, { label: string; color: string; hint: string }> = {
  ReportHeader:  { label: 'Report Header',  color: '#6366f1', hint: 'Once at start' },
  PageHeader:    { label: 'Page Header',    color: '#0ea5e9', hint: 'Every page top' },
  GroupHeader:   { label: 'Group Header',   color: '#8b5cf6', hint: 'Group start' },
  Detail:        { label: 'Detail',         color: '#22c55e', hint: 'Repeats per row' },
  SubDetail:     { label: 'Sub Detail',     color: '#84cc16', hint: 'Nested detail' },
  GroupFooter:   { label: 'Group Footer',   color: '#a855f7', hint: 'Group end' },
  PageFooter:    { label: 'Page Footer',    color: '#06b6d4', hint: 'Every page bottom' },
  ReportFooter:  { label: 'Report Footer',  color: '#f59e0b', hint: 'Once at end' },
};

// ── Helpers ──────────────────────────────────────────────────

let _counter = 0;
export function uid(): string {
  return `el_${Date.now()}_${++_counter}`;
}

export function createDefaultTemplate(): DesignTemplate {
  return {
    id: uid(),
    name: 'Untitled Template',
    pageSize: 'A4',
    orientation: 'portrait',
    margins: { top: 15, right: 15, bottom: 20, left: 15 },
    bands: [
      { id: uid(), type: 'PageHeader',   height: 60,  elements: [] },
      { id: uid(), type: 'Detail',       height: 200, elements: [] },
      { id: uid(), type: 'PageFooter',   height: 40,  elements: [] },
    ],
  };
}
